import { track } from "@/features/analytics";
import {
  setLiveSyncProbe,
  setPresentPeerIds,
} from "@/features/collaboration/last-known-pointer";
import { addCollabBreadcrumb } from "@/shared/utils/sentry-helpers";
import type { FlatContentLayersType, StoryType } from "@config";
import { useRoom } from "@liveblocks/react/suspense";
import { useAppDispatch, useAppSelector, type RootState } from "@store";
import {
  editEntity,
  restoreActiveFromHistory,
  setCollabHistoryStatus,
  setVersionDataValue,
  syncFromYjs,
} from "@store/story";
import { getEntities, getRestStory } from "@store/story/selectors";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import { useStore } from "react-redux";
import * as Y from "yjs";
import { useHocuspocusToken } from "../hooks/use-hocuspocus-token";
import { StoryHistoryContext } from "../hooks/useStoryHistory";
import {
  captureScrollAnchor,
  restoreScrollAnchor,
} from "../utils/preserve-scroll-anchor";
import { yieldToMain } from "../utils/yield-to-main";
import { settleCollabHistoryStep } from "./collab-history-step";
import { mapLiveblocksEventToYjsOperation } from "../yjs/map-liveblocks-event-to-yjs-operation";
import { observeEntityChanges } from "../yjs/observe-entity-changes";
import { LOCAL_ORIGIN } from "../yjs/origins";
import { createRemoteApplyQueue } from "../yjs/remote-apply-queue";
import {
  applyStoryOperationToYDoc,
  getYjsStorySnapshot,
  migrateDegenerateOrders,
  settingsFromY,
  writeStoryToDoc,
} from "../yjs/schema";
import { seedYDocFromGcs } from "../yjs/seed-from-gcs";
import { createHocuspocusTransport } from "../yjs/transport/create-hocuspocus-transport";
import { useReportYjsTransportStatus } from "../yjs/transport/status-context";
import type { YjsTransport } from "../yjs/transport/types";
import type { YjsCollaborationBridge } from "../yjs/types";
import {
  createAiBatchCapture,
  createStoryUndoManager,
} from "../yjs/undo-manager";
import { YjsSyncContext } from "../yjs/yjs-sync-context";

// A single remote-apply commit costing more than this leaves a Sentry
// breadcrumb, so any captured error carries the apply-cost trail. The fix's
// production verification is the Sentry long-animation-frame metric itself:
// spans with browser.script.invoker DOMWebSocket.onmessage (pre-fix baseline
// avg 5.8s among >2s frames, wk of 2026-07-27) should drop after deploy.
const SLOW_REMOTE_APPLY_MS = 200;

export const YjsStoryProvider = ({ children }: { children: ReactNode }) => {
  const room = useRoom();
  const dispatch = useAppDispatch();
  // The store itself, not a selector: the AI-batch capture below must learn
  // that a turn began in the SAME tick as pushAiCheckpoint, because handlers
  // dispatch their first edits right after it and the bridge writes them to
  // the Y.Doc synchronously, mid-reducer (broadcastEvent → applyEvent). A
  // useAppSelector + effect would run after React's render — too late.
  const store = useStore<RootState>();
  const getHocuspocusToken = useHocuspocusToken();
  const reportTransportStatus = useReportYjsTransportStatus();
  const story = useAppSelector(getRestStory) as StoryType | null;
  const flatContentLayers = useAppSelector(getEntities);
  const activeEntityId = useAppSelector((s) => s.story.activeEntityId);
  const activeThumbnailId = useAppSelector((s) => s.story.activeThumbnailId);
  const storyRef = useRef<StoryType | null>(story);
  const flatRef = useRef(flatContentLayers);
  // Kept fresh so the UndoManager can stash the current selection + slide on
  // each undo step and restore them when that step is undone/redone.
  const activeEntityIdRef = useRef(activeEntityId);
  // The slide the user is viewing (activeThumbnailId drives the rendered slide).
  // Stashed so a popped step can return the user here if the entity is gone.
  const activeThumbnailIdRef = useRef(activeThumbnailId);
  const undoManagerRef = useRef<Y.UndoManager | null>(null);
  const transportRef = useRef<YjsTransport | null>(null);
  const applyQueueRef = useRef<{ flushNow: () => void } | null>(null);
  const [synced, setSynced] = useState(false);
  const [history, setHistory] = useState({
    canUndo: false,
    canRedo: false,
    topAiActionId: null as string | null,
  });

  // Publish calls this before snapshotting so it never captures a doc that's
  // mid-sync. A short settle first lets any in-flight PEER ops arrive over the
  // socket (they reconcile into Redux), then we wait for our OWN pending writes
  // to be acknowledged by Hocuspocus, bounded by timeoutMs.
  const waitForConverged = useCallback(async (timeoutMs = 5000) => {
    const start = performance.now();
    await new Promise((resolve) => setTimeout(resolve, 150));
    const isConverged = () => transportRef.current?.isConverged() ?? false;
    while (!isConverged() && performance.now() - start < timeoutMs) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return isConverged();
  }, []);

  storyRef.current = story;
  flatRef.current = flatContentLayers;
  activeEntityIdRef.current = activeEntityId;
  activeThumbnailIdRef.current = activeThumbnailId;

  // Collab undo/redo go through the Yjs UndoManager and reconcile via the
  // observer ("remote") path, which doesn't bump changeId — so the autosave
  // wouldn't fire and the durable contentPointer would stay pre-undo (refresh
  // reverts the undo). Dispatch markContentDirty AFTER the (synchronous, remote-
  // origin) reconcile so the autosave persists the post-undo state. Guarded on
  // the returned StackItem so an empty-stack no-op doesn't spuriously save. Safe
  // re: undo pollution because the autosave is now broadcast:true (no Y.Doc op).
  // The inverse op reaches Redux through the remote-apply queue (undo-manager
  // transactions are non-local origin). Drain it synchronously so the microtask
  // in onStackItemPopped that restores the pre-undo selection runs AFTER the
  // reconcile — a frame-deferred reconcile would clobber the restored selection.
  // settleCollabHistoryStep also raises isInRedoUndo, so an open Froala takes
  // the restored content (see collab-history-step.ts).
  const undo = useCallback(() => {
    if (undoManagerRef.current?.undo()) {
      applyQueueRef.current?.flushNow();
      settleCollabHistoryStep(dispatch);
    }
  }, [dispatch]);
  const redo = useCallback(() => {
    if (undoManagerRef.current?.redo()) {
      applyQueueRef.current?.flushNow();
      settleCollabHistoryStep(dispatch);
    }
  }, [dispatch]);

  const syncStatus = useMemo(
    () => ({ enabled: true, synced, waitForConverged }),
    [synced, waitForConverged],
  );
  const historyValue = useMemo(
    () => ({
      active: true,
      canUndo: history.canUndo,
      canRedo: history.canRedo,
      topAiActionId: history.topAiActionId,
      undo,
      redo,
    }),
    [history.canUndo, history.canRedo, history.topAiActionId, undo, redo],
  );

  // Publish who is in the room so a parked autosave can tell a LIVE co-editor's
  // head (adopt it, our edits are already in the shared Y.Doc) from a stale lone
  // tab (reload). See "Present co-editors" in last-known-pointer.ts.
  useEffect(() => {
    const publish = () =>
      setPresentPeerIds(room.getOthers().map((other) => other.id));
    publish();
    const unsubscribe = room.subscribe("others", publish);
    return () => {
      unsubscribe();
      setPresentPeerIds([]);
    };
  }, [room]);

  useEffect(() => {
    let disposed = false;
    let seeded = false;
    let seeding = false;
    const doc = new Y.Doc();
    // Liveblocks Room stays mounted for presence; Hocuspocus is the sole Yjs transport.
    const roomId = room.id;
    const { transport } = createHocuspocusTransport({
      doc,
      roomId,
      getToken: getHocuspocusToken,
    });
    transportRef.current = transport;
    // Lets a parked autosave check "connected and nothing pending" before it
    // adopts a live co-editor's head (see last-known-pointer.ts).
    setLiveSyncProbe(() => transport.isConverged());
    transport.onStatus(reportTransportStatus);

    // Collaborative undo/redo. Tracks only LOCAL_ORIGIN changes (this user's
    // own edits) and computes inverses on the CRDT itself; the inverse flows
    // back to Redux + peers through the same observer path as a remote change.
    const undoManager = createStoryUndoManager(doc);
    undoManagerRef.current = undoManager;

    // ONE AI TURN IS ONE UNDO STEP here too. The Redux reducer collapses a
    // turn's frames by aiActionId (handleUndo), but in a room that reducer
    // never runs — undo is this manager, which splits a turn wherever its
    // writes land more than captureTimeout apart or a structural op asks for
    // a boundary (a bulk edit of seven texts undid one at a time). While the
    // store's activeAiActionId is set, the capture holds the manager open and
    // the structural boundaries below are skipped, so the whole turn is one
    // stack item, tagged with the turn's id (see undo-manager.ts).
    const aiBatch = createAiBatchCapture(undoManager);
    const syncAiBatch = () =>
      aiBatch.sync(store.getState().story.history.activeAiActionId);
    syncAiBatch();
    const unsubscribeStore = store.subscribe(syncAiBatch);

    // The AI Undo affordances read the undo stack through Redux
    // (history-selectors.ts) because the agent's undo command
    // (undo-last-change.ts) reads it outside StoryHistoryContext — so the real stack top is mirrored into
    // the store on every stack change. Deferred: stack-item-added fires
    // inside the Y.Doc transaction that applyEvent runs MID-REDUCER for a
    // local edit, and dispatching there is illegal (same reason
    // scheduleLocalReconcile defers). Coalesced, with values read at flush.
    let mirrorPending = false;
    const mirrorHistoryToStore = () => {
      if (mirrorPending) return;
      mirrorPending = true;
      queueMicrotask(() => {
        mirrorPending = false;
        if (disposed) return;
        dispatch(
          setCollabHistoryStatus({
            canUndo: undoManager.canUndo(),
            canRedo: undoManager.canRedo(),
            topAiActionId: aiBatch.topAiActionId(),
          }),
        );
      });
    };

    const refreshHistory = () => {
      if (disposed) return;
      setHistory({
        canUndo: undoManager.canUndo(),
        canRedo: undoManager.canRedo(),
        topAiActionId: aiBatch.topAiActionId(),
      });
      mirrorHistoryToStore();
    };
    const onStackItemAdded = (event: {
      stackItem: { meta: Map<string, unknown> };
      type: "undo" | "redo";
    }) => {
      // Stash the selection AND the slide the user is on, so an undo can return
      // them to where they were — even if the entity itself no longer exists.
      event.stackItem.meta.set("activeEntityId", activeEntityIdRef.current);
      event.stackItem.meta.set("activeSlideId", activeThumbnailIdRef.current);
      // And, during an AI turn, the turn's id — merged writes fire
      // stack-item-updated instead, so the tag lands once on the one item.
      // Undo-stack pushes only: this event also fires for the redo stack (an
      // undo mid-turn pushes there), and topAiActionId() reads the undo stack.
      if (event.type === "undo") aiBatch.tag(event.stackItem);
      refreshHistory();
    };
    const onStackItemPopped = (event: {
      stackItem: { meta: Map<string, unknown> };
    }) => {
      const entityId = event.stackItem.meta.get("activeEntityId");
      const slideId = event.stackItem.meta.get("activeSlideId");
      // Restore the selection captured when this step was created. Deferred to a
      // microtask so it runs AFTER the observer-driven reconcile (syncFromYjs),
      // which would otherwise reset selection to the first slide when the
      // just-undone entity no longer exists. restoreActiveFromHistory reselects
      // the entity if it survived, else returns the user to the slide they were
      // on (instead of yanking them to slide 1).
      queueMicrotask(() => {
        if (disposed) return;
        dispatch(
          restoreActiveFromHistory({
            entityId: typeof entityId === "string" ? entityId : null,
            slideId: typeof slideId === "string" ? slideId : null,
          }),
        );
      });
      refreshHistory();
    };
    undoManager.on("stack-item-added", onStackItemAdded);
    undoManager.on("stack-item-popped", onStackItemPopped);
    undoManager.on("stack-cleared", refreshHistory);
    // Seed the mirror now (legal here — an effect, not a reducer) so
    // history.collab is never null between mount and the first stack event;
    // otherwise the AI Undo toast briefly gates on the Redux frame while a
    // collab undo would run against an empty stack.
    mirrorHistoryToStore();

    const bridge: YjsCollaborationBridge = {
      enabled: true,
      applyEvent: (event, payload) => {
        const result = mapLiveblocksEventToYjsOperation(event, payload, {
          flatContentLayers: flatRef.current,
          storySettings: storyRef.current?.settings,
        });
        if (!result) return false;
        // The mapper may return a single op or an array (e.g. undo of a
        // multi-entity action). Apply them all atomically in one Y.Doc
        // transaction so peers see one merged change with one LOCAL_ORIGIN
        // tag and one observer callback.
        const ops = Array.isArray(result) ? result : [result];
        if (ops.length === 0) return false;
        // Discrete structural actions get their own undo step: stop capturing
        // before (so they don't merge with prior grouped edits) and after (so
        // subsequent edits don't merge into them). Never inside an AI turn —
        // a turn that inserts a slide, or restyles then reorders, stays ONE
        // step (aiBatch decides).
        const boundary = aiBatch.shouldStopCapturingFor(
          ops.map((op) => op.type),
        );
        if (boundary) undoManager.stopCapturing();
        doc.transact(() => {
          for (const op of ops) applyStoryOperationToYDoc(doc, op);
        }, LOCAL_ORIGIN);
        if (boundary) undoManager.stopCapturing();
        return true;
      },
    };
    window.SD_YJS_COLLABORATION = bridge;

    // Whole-tree reconcile: rebuild flatContentLayers from the merged Y.Doc.
    // Used for any structural change (reorder/add/delete) and remote story
    // changes; syncFromYjs preserves local selection & history.
    const reconcileFromDoc = () => {
      if (disposed) return;
      const snapshot = getYjsStorySnapshot(doc);
      dispatch(
        syncFromYjs({
          story: snapshot.story as unknown as Record<string, unknown>,
          flatContentLayers:
            snapshot.flatContentLayers as unknown as FlatContentLayersType,
        }),
      );
    };

    // Apply a remote change with the viewport held steady. ANY remote change
    // can move content above the local user's position — a structural
    // add/delete, an edit that changes an entity's height (peer pastes a
    // paragraph, image resizes), a variable value that reflows text — and the
    // editor scrolls the WINDOW with no scroll anchoring, so the user's screen
    // visibly jumps. So: capture the deepest visible entity as an anchor,
    // commit the change synchronously (flushSync — legal here, we're in a
    // websocket/observer callback, never inside React rendering), then scroll
    // by the anchor's displacement. scrollTop changes; what the user is
    // looking at does not move — all within one frame, no flicker. Errors must
    // never block the apply itself — anchoring is cosmetic, the sync is not.
    const withScrollAnchor = (apply: () => void) => {
      let applied = false;
      try {
        const anchors = captureScrollAnchor();
        flushSync(() => {
          apply();
          applied = true;
        });
        restoreScrollAnchor(anchors);
      } catch {
        // Whatever failed, the remote change MUST still land in Redux.
        if (!applied) apply();
      }
    };

    // Local structural ops are applied to the Y.Doc *synchronously inside the
    // Redux reducer* (broadcastEvent → bridge.applyEvent runs mid-dispatch), so
    // the observer fires while a reducer is still executing. Dispatching there
    // is illegal, so defer the reconcile to a microtask and coalesce a burst of
    // structural writes in the same tick into a single rebuild.
    let localReconcilePending = false;
    const scheduleLocalReconcile = () => {
      if (localReconcilePending) return;
      localReconcilePending = true;
      queueMicrotask(() => {
        localReconcilePending = false;
        reconcileFromDoc();
      });
    };

    // Remote changes apply once per frame, not once per op. Applying each op
    // synchronously here meant the whole burst ran inside the same
    // DOMWebSocket.onmessage task — measured blocking the main thread for
    // seconds during co-editing (the "editor freezes when two people have the
    // story open" reports). The batch applies with ONE scroll-anchored
    // synchronous commit; values are read fresh from the doc at flush time so
    // coalescing never applies stale intermediate states.
    const applyQueue = createRemoteApplyQueue({
      apply: ({ structural, editIds, versionDataKeys }) => {
        if (disposed) return;
        withScrollAnchor(() => {
          if (structural) {
            // Whole-tree reconcile covers every entity, including queued edits.
            reconcileFromDoc();
          } else {
            for (const entityId of editIds) {
              const entityMap = doc
                .getMap<Y.Map<unknown>>("entities")
                .get(entityId);
              if (!entityMap) continue;
              const settings = settingsFromY(entityMap.get("settings"));
              // broadcast:true prevents re-echo to the doc.
              dispatch(
                editEntity({
                  id: entityId,
                  data: settings,
                  full: true,
                  broadcast: true,
                }),
              );
            }
          }
          // version.data lives outside the reconcile snapshot — always applied.
          for (const key of versionDataKeys) {
            dispatch(
              setVersionDataValue({
                key,
                value: doc.getMap("versionData").get(key),
                broadcast: true,
              }),
            );
          }
        });
      },
      onMetrics: ({ ms, ops, structural }) => {
        if (disposed || ms < SLOW_REMOTE_APPLY_MS) return;
        const rounded = Math.round(ms);
        addCollabBreadcrumb("remote apply flush slow", {
          ms: rounded,
          ops,
          structural,
        });
      },
    });
    applyQueueRef.current = applyQueue;

    const unsubscribeObserver = observeEntityChanges(doc, {
      onEntityChange: (event) => {
        if (disposed) return;

        // OBSERVE-ONLY diagnostic (no behavior change). When we're ALONE in the
        // room there should be no remote editor, so a remote-origin change is
        // suspicious — it may be a divergent/stale server Y.Doc (reconnect
        // resyncing pre-edit state, or a server copy that never got our edits),
        // which is the suspected cause of the live "story reverted on its own"
        // report (Jam 8456fab2). We do NOT block it here — blocking risks
        // legitimate flows (e.g. a reset/restore is local-origin so it wouldn't
        // be caught, but a stale-server merge and a real concurrent change can
        // look alike) and leaves the doc diverged. Instead we record it so the
        // next occurrence is captured with evidence; the durable-side monotonic
        // guard is the actual protection. Once a real occurrence confirms this
        // path fires, we add a targeted fix.
        if (event.origin === "remote" && room.getOthers().length === 0) {
          addCollabBreadcrumb(
            "remote change while alone (suspect stale-server)",
            {
              kind: event.kind,
            },
          );
        }

        // Local events reaching here are structural/add/delete only (local
        // edits are suppressed by the observer, local story/version-data are
        // filtered there too). They must wait for the in-flight reducer to
        // finish, and need no scroll anchoring: the user made the change.
        if (event.origin === "local") {
          scheduleLocalReconcile();
          return;
        }

        if (event.kind === "version-data") {
          // A peer edited a dynamic-variable value (in-editor popover).
          applyQueue.enqueue({ kind: "version-data", key: event.key });
          return;
        }
        if (event.kind === "edit") {
          applyQueue.enqueue({ kind: "edit", entityId: event.entityId });
          return;
        }
        // structural / add / delete / story → whole-tree reconcile at flush.
        applyQueue.enqueue({ kind: "structural" });
      },
    });

    // Seeds the local Y.Doc on first sync and produces the first authoritative
    // snapshot. Extracted from the onSync handler so a thrown seed (e.g. a
    // failed or timed-out durable GCS fetch — seedYDocFromGcs throws by design)
    // is caught there instead of becoming an unhandled rejection that leaves the
    // editor stuck in its loading skeleton forever.
    const runInitialSeed = async () => {
      const seedStart = performance.now();
      let seedSource: "redux" | "gcs" | "server" = "server";
      let applyMs = 0;
      // The "sync" event fires synchronously inside DOMWebSocket.onmessage,
      // right after Y.applyUpdate of the entire server doc — without a yield
      // every phase below stacks onto that same task (measured multi-second
      // main-thread blocks on story open). Yield first so the browser can
      // paint/respond, then decide.
      await yieldToMain();
      if (disposed) return;
      // Re-entry data-loss guard. When we're ALONE in the room, the durable
      // contentPointer (kept fresh by the REST autosave, independent of the
      // WebSocket) is authoritative. The Liveblocks server Y.Doc can fall
      // behind if the socket lagged while editing — earlier we adopted it
      // unconditionally on re-entry, which silently reverted recent work
      // (sole-editor: leave story → return → ~15min of edits gone, recoverable
      // only via version history). So when alone, REPLACE the doc with the
      // durable content rather than trusting whatever the server synced.
      //
      // When peers ARE present, their live Y.Doc is the authority — it holds
      // edits not yet flushed to the durable pointer — so we adopt it as before.
      const others = room.getOthers();
      if (others.length === 0) {
        // Seed the doc from the DURABLE contentPointer (authoritative when alone).
        // CRITICAL: wait for the durable source to be loaded before deciding. On a
        // refresh the WebSocket "sync" can beat the REST story load (useGetStory);
        // if we decided right now we'd see empty Redux + a null pointer, skip the
        // durable replace, and silently adopt whatever the (possibly cold/stale)
        // Liveblocks server doc holds — then the autosave bakes that stale/initial
        // content forward, and the NEXT load's writeStoryToDoc even overwrites the
        // shared server doc with it (clear+rewrite syncs to Liveblocks). So poll
        // until the durable content or pointer is available; the editor already
        // shows a skeleton meanwhile, and it's bounded so a genuinely new/empty
        // story can't hang. (Monday #12337353624)
        const DURABLE_WAIT_MS = 8000;
        const start = performance.now();
        let durablePointer: string | null = null;
        let reduxHasContent = false;
        while (performance.now() - start < DURABLE_WAIT_MS) {
          const s = storyRef.current;
          const f = flatRef.current;
          if (s && f && Object.keys(f).length > 0) {
            reduxHasContent = true;
            break;
          }
          if (s?.settings?.contentPointer) {
            durablePointer = s.settings.contentPointer;
            break;
          }
          if (disposed) return;
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        if (reduxHasContent) {
          // writeStoryToDoc CLEARS the maps first, so a stale server doc is truly
          // replaced (entities deleted in the durable version are dropped, not
          // merged back).
          seedSource = "redux";
          const currentStory = storyRef.current;
          const currentFlat = flatRef.current;
          addCollabBreadcrumb("seed: replaced doc from durable Redux content", {
            storyId: currentStory?._id ?? null,
            entities: currentFlat ? Object.keys(currentFlat).length : 0,
          });
          const applyStart = performance.now();
          doc.transact(() => {
            writeStoryToDoc(doc, {
              ...(currentStory as unknown as Record<string, unknown>),
              flatContentLayers: currentFlat as never,
            });
          }, LOCAL_ORIGIN);
          applyMs = performance.now() - applyStart;
        } else if (durablePointer) {
          seedSource = "gcs";
          addCollabBreadcrumb(
            "seed: replaced doc from durable contentPointer (GCS)",
          );
          const applyStart = performance.now();
          await seedYDocFromGcs(doc, durablePointer, {
            replace: true,
            // A peer can join and sync their live state to us while the GCS
            // fetch is in flight; replacing then would clobber their edits.
            // Only apply while we're still alone.
            shouldApply: () => room.getOthers().length === 0,
          });
          applyMs = performance.now() - applyStart;
          if (disposed) return;
        } else {
          // No durable content/pointer after the wait. Either a brand-new/empty
          // story (the server doc IS the correct fresh seed) or REST genuinely
          // failed to load the story. We deliberately do NOT writeStoryToDoc, so
          // we never clobber the server doc with empty content — the snapshot read
          // below adopts whatever the server holds as a last resort. Surfaced so a
          // real "durable load failed on an existing deck" case is visible.
          addCollabBreadcrumb(
            "seed: durable source unavailable, kept server doc",
            {
              storyId: storyRef.current?._id ?? null,
            },
          );
        }
      }
      // The doc write above and the snapshot+dispatch below are each heavy on
      // large decks — split them into separate tasks. A remote update landing
      // in this gap is safe: the snapshot below reads the merged doc.
      await yieldToMain();
      if (disposed) return;

      // Self-heal rooms persisted before the `order` field existed: their
      // entities decode with order "" (all siblings tied), which breaks
      // reorder/insert positioning. Re-key in place so no manual room wipe is
      // needed. No-op for freshly-seeded rooms (their keys are already healthy).
      doc.transact(() => migrateDegenerateOrders(doc), LOCAL_ORIGIN);

      const snapshotStart = performance.now();
      const initialSnapshot = getYjsStorySnapshot(doc);
      dispatch(
        syncFromYjs({
          story: initialSnapshot.story as unknown as Record<string, unknown>,
          flatContentLayers:
            initialSnapshot.flatContentLayers as unknown as FlatContentLayersType,
        }),
      );
      const snapshotMs = performance.now() - snapshotStart;
      // The seed (from Redux or GCS) is applied with LOCAL_ORIGIN and would
      // otherwise become an undoable step — Ctrl+Z could wipe the whole story.
      // Discard the history accumulated during load so the first undoable step
      // is the user's first real edit.
      undoManager.clear();
      refreshHistory();

      // Per-open timing breadcrumb: rides along on any captured error, so a
      // freeze/crash report shows how expensive this open's seed was. The
      // aggregate verification lives in Sentry's own long-animation-frame
      // spans (invoker DOMWebSocket.onmessage) — no analytics event needed.
      addCollabBreadcrumb("seed: applied", {
        totalMs: Math.round(performance.now() - seedStart),
        applyMs: Math.round(applyMs),
        snapshotMs: Math.round(snapshotMs),
        entities: Object.keys(initialSnapshot.flatContentLayers ?? {}).length,
        source: seedSource,
      });
    };

    transport.onSync(async (isSynced: boolean) => {
      if (!isSynced || disposed) return;
      // Hocuspocus re-emits "sync: true" after every reconnect, not just the
      // initial connection. The seed and undo-history reset must run only once:
      // `seeded` latches a completed seed; `seeding` guards re-entry while the
      // async seed is still in flight (a reconnect mid-seed would otherwise run
      // it twice). Updates missed while offline arrive as ordinary Yjs updates
      // via observeEntityChanges, so the reconnect path needs no extra reconcile.
      if (seeded || seeding) return;
      seeding = true;
      try {
        await runInitialSeed();
        if (disposed) return;
        seeded = true;
        // The first full editor render (unlocked by setSynced) is the single
        // heaviest task of story open — give the seed's own commit a task
        // boundary first instead of stacking both into one multi-second block.
        await yieldToMain();
        if (disposed) return;
        // Signal downstream loaders (LoadContentProvider) that Yjs has produced
        // its first authoritative snapshot. Until this fires the editor stays in
        // its loading state so the user never sees the REST-based view briefly
        // replaced by the Yjs view.
        setSynced(true);
      } catch (error) {
        // The seed failed (durable GCS replace, snapshot build, or doc write).
        // Deliberately do NOT setSynced(true): forcing the editor open here would
        // bake whatever empty/stale content the doc holds forward — exactly the
        // blank-story regression the durable-content guard exists to prevent.
        // Leave `seeded` false so the next "synced" emit (Hocuspocus re-emits on
        // every reconnect) retries the seed; the editor keeps its loading
        // skeleton meanwhile rather than showing wrong content.
        const message = error instanceof Error ? error.message : String(error);
        addCollabBreadcrumb("seed: failed, will retry on next sync", {
          storyId: storyRef.current?._id ?? null,
          error: message,
        });
        track("collab seed failed", { error: message });
      } finally {
        seeding = false;
      }
    });

    return () => {
      disposed = true;
      applyQueue.dispose();
      applyQueueRef.current = null;
      unsubscribeObserver();
      unsubscribeStore();
      undoManager.destroy();
      undoManagerRef.current = null;
      transport.destroy();
      transportRef.current = null;
      setLiveSyncProbe(null);
      doc.destroy();
      delete window.SD_YJS_COLLABORATION;
      // No room, no mirror: the selectors fall back to the Redux history.
      dispatch(setCollabHistoryStatus(null));
    };
  }, [room, store, dispatch, getHocuspocusToken, reportTransportStatus]);

  return (
    <YjsSyncContext.Provider value={syncStatus}>
      <StoryHistoryContext.Provider value={historyValue}>
        {children}
      </StoryHistoryContext.Provider>
    </YjsSyncContext.Provider>
  );
};
